import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

/**
 * Catch-all "Gone" page used for retired Wix URLs (e.g. /terms-conditions)
 * and for any other unknown path. Adds noindex,nofollow meta so crawlers
 * do not index it. Note: the underlying HTTP status remains 200 because the
 * frontend is a SPA — a true 410 HTTP status requires a hosting-layer rule
 * (see project notes).
 */
export default function GonePage() {
  const { t } = useT();
  useEffect(() => {
    document.title = "Page no longer available · Coumba Kandji Diamonds";

    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);

    const status = document.createElement("meta");
    status.httpEquiv = "Status";
    status.content = "410 Gone";
    document.head.appendChild(status);

    return () => {
      document.head.removeChild(robots);
      document.head.removeChild(status);
    };
  }, []);

  return (
    <main
      data-testid="gone-page"
      className="relative bg-soft text-ink pt-32 lg:pt-40 pb-28 md:pb-40 overflow-hidden min-h-[70vh] flex items-center"
    >
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-ice_blue/30 blur-3xl opacity-60" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ice_blue to-transparent" />
      </div>

      <div className="container-luxe">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-12 bg-gold" />
            <span className="overline">{t("gone.overline")}</span>
          </div>

          <h1 className="h-display text-4xl md:text-5xl lg:text-6xl text-ink text-balance leading-[1.02]">
            {t("gone.title1")}
            <br />
            <span className="italic font-light text-ink/90">
              {t("gone.title2")}
            </span>
          </h1>

          <p className="mt-8 text-base md:text-lg leading-relaxed text-muted_text font-light max-w-md">
            {t("gone.body")}
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link
              to="/"
              data-testid="gone-back-home"
              className="group inline-flex items-center gap-3 bg-ink text-white px-8 py-4 font-mono text-[11px] uppercase tracking-[0.32em] hover:bg-graphite transition-all duration-500"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.4} />
              {t("gone.ctaHome")}
            </Link>
            <Link
              to="/legal-notice"
              className="group inline-flex items-center gap-3 border border-ink/30 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.32em] text-ink hover:border-ink transition-all duration-500"
            >
              {t("gone.ctaLegal")}
              <span
                aria-hidden
                className="inline-block transition-transform duration-500 group-hover:translate-x-1"
              >
                →
              </span>
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
